import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import makeStyles from "@mui/styles/makeStyles";
import PropTypes from "prop-types";
import clsx from "clsx";
import _, {isArray, filter, orderBy} from 'lodash';
import moment from "moment";

import {Grid, Paper} from '@mui/material';
import {
  getAssignedSlotTileId,
  getVisibleWidth,
} from '../../../utils/edReviewUtils';
import TimeSlots, { TimeColumn } from "./calendarTiles/timeSlots";
import CurrentTimeHorizontalLine from "./calendarTiles/timeHorizontalLine";
import DailyCalendarColumns from "./columns/dailyCalendarColumns";
import ColumnHeader from "./columns/columnHeader/columnHeader";


export const useDailyCalendarStyles = makeStyles((theme) => ({
  timeContainer: {
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: 405,
    overflow: "hidden",
    position: "sticky",
    flexBasis: DEFAULT_LEFT,
    borderRight: `1px solid ${theme.palette.background.paperHover}`,
  },
  timelineGrid: {
    flexWrap: "nowrap",
    position: "relative",
    borderRight: `1px solid${theme.palette.background.paperHover}`,
  },
  timelineContainer: {
    flex: "1 0 0",
    borderLeft: "none",
    flexWrap: "nowrap",
    overflow: "hidden",
    position: "relative",
    backgroundImage: `linear-gradient(to bottom, ${theme.palette.background.paperHover} 1px, transparent 1px)`,
  },
  timelineSlot: {
    width: "100%",
    position: "absolute",

    "&.borderBottom": {
      height: 1,
      borderBottom: `1px solid ${theme.palette.background.paperHover}`,
    },
  },
  time: {
    width: "70px",
    fontWeight: "bold",
    textAlign: "center",
    verticalAlign: "middle",
    backgroundColor: theme.palette.background.default,

    "&.odd": {
      color: "grey",
    },
  },
  event: {
    flex: "1",
    position: "absolute",
    backgroundColor: "lightblue",
  },
  currentTimeLine: {
    height: 2,
    position: "absolute",
    backgroundColor: theme.palette.primary.main,
  },
  currentTimeLineContainer: {
    height: 30,
    color: "#FFF",
    padding: "4px",
    paddingLeft: "5px",
    position: "absolute",
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
    backgroundColor: theme.palette.primary.main,
  },
  paper: {
    flexGrow: 1,
    borderRadius: 10,
    position: "relative",
    boxShadow: 'none',
    background: `repeating-linear-gradient(-60deg, ${theme.palette.background.paperDarker}, ${theme.palette.background.paperDarker} 20px, ${theme.palette.background.default} 20px, ${theme.palette.background.default} 40px)`,
  },
  columnHeader: {
    top: 0,
    zIndex: 900,
    minHeight: 50,
    paddingTop: 5,
    paddingLeft: 70,
    position: "sticky",
    alignItems: "flex-start",
    minWidth: "100%",
    width: "fit-content",
    backgroundColor: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.background.paper}`,

    "&::before": {
      left: 0,
      top: 60,
      height: 1,
      width: 70,
      content: '""',
      display: "block",
      position: "absolute",
      backgroundColor: theme.palette.background.default,
    },
  },
  columnClinicianHeader: {
    minHeight: 50,
    display: "flex",
    borderRadius: 7,
    marginBottom: 10,
    // padding: "0 10px",
    gap: 10,
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: theme.palette.background.paperDarker,
  },
  specialty: {
    marginRight: 10,
    borderRadius: 7,
    padding: "3px 5px",
    width: "fit-content",
    border: `1px solid ${theme.palette.mode === "dark" ? theme.palette.background.paperHover : theme.palette.background.paper}`,
  },
  dropZone: {
    "&.is-over": {
      backgroundColor: "rgba(0, 0, 255, .2)",
    },
  },
  dropZoneIcon: {
    "&.can-drop": {
      background: "grey !important",
    },
    "&.is-over": {
      // opacity: 0.7,
    },
  },
}));

// export const SLOT_HEIGHT = 120;
export const SLOT_HEIGHT = 150;
export const DEFAULT_LEFT = 70;
export const LEFT_OFFSET = 2;
export const OFFSET = 2;
export const COLUMN_GAP = 8;

const MIN_CONTAINER_WIDTH = 350;

const EdReviewCalendarDaily = ({ date, slots, sessions }) => {
  const classes = useDailyCalendarStyles();

  // Current timeline
  const currenLine = useRef(null);
  const refTimeline = useRef(null);
  const [currentTime, setCurrentTime] = useState(moment());

  const timelineVisibleWidth = getVisibleWidth(refTimeline.current) - (DEFAULT_LEFT + 20) || 0;

  const OFFSET_LEFT = 0;

  // Current implementation
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     setCurrentTime(moment());
  //   // }, 1000);
  //   }, 60000);
  //
  //   if (currenLine.current)
  //     currenLine.current.scrollIntoView({
  //       behavior: "smooth",
  //       block: "center",
  //     });
  //
  //   return () => {
  //     clearInterval(interval);
  //   };
  // }, [currenLine.current, refTimeline.current]);

  // Implementation -> if today -> scroll to current hour, then scroll to earliest booked slot
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(moment());
    }, 60000);

    return () => {
      clearInterval(interval);
    };
  }, [currenLine.current, refTimeline.current]);

  useEffect(() => {
    let elToScrollInto;

    if (moment(date).isSame(moment(), 'day')) {
      elToScrollInto = currenLine.current;
    } else if (slots.length > 0) {
      const earliestSession = orderBy(sessions, (sess) => moment(sess.startDate).format("HH:mm")).at(0);
      const earliest = filter(orderBy(slots, ['startDate']), sl => sl?.status !== 'locked').at(0);
      const earliestTileId = getAssignedSlotTileId(earliest);
      elToScrollInto = document.getElementById(earliestTileId);
    } else {
      const earliestSession = orderBy(sessions, (sess) => moment(sess.startDate).format("HH:mm")).at(0);
      elToScrollInto = document.getElementById(`${earliestSession?.sessionId}${moment(earliestSession?.startDate).format("HH:mm")}`);
    }

    if (elToScrollInto) elToScrollInto.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
  }, [currenLine.current, slots, date, sessions])

  const calculateTopForCurrentTime = (time) => {
    const startOfDay = moment(currentTime).startOf("day");
    const minutesFromStartOfDay = currentTime.diff(startOfDay, "minutes");
    const top = (minutesFromStartOfDay / 30) * SLOT_HEIGHT - (time ? 15 : 0);
    return `${top}px`;
  };

  const getColumnWidth = (cols) => {
    let columns = cols;
    if (!_.isArray(cols)) {
      columns = [];
    }

    const width = timelineVisibleWidth / (columns.length || 1);

    return width > MIN_CONTAINER_WIDTH ? width : MIN_CONTAINER_WIDTH;
  };

  const columnWidth = useMemo(() => getColumnWidth(sessions), [sessions.length, refTimeline.current?.getBoundingClientRect()?.width]);

  return (
    <Paper className={classes.paper}>
      <ColumnHeader
        columns={sessions}
        columnHeaderStyles={{
          minWidth: MIN_CONTAINER_WIDTH,
          width: columnWidth,
        }}
      />

      {/*<div id="timeline-calendar">*/}
        <Grid
          container
          ref={refTimeline}
          id="timeline-calendar"
          className={classes.timelineGrid}
        >
          <Grid item className={classes.timeContainer}>
            <TimeColumn />

            <CurrentTimeHorizontalLine
              date={date}
              ref={currenLine}
              onlyTimeIndicator
              leftOffset={OFFSET_LEFT}
              yAxisFromTop={calculateTopForCurrentTime}
              width={refTimeline.current?.getBoundingClientRect()?.width}
            />
          </Grid>

          <Grid
            item
            gap={1}
            container
            // ref={timelineContainerRef}
            className={clsx(classes.timelineContainer, "scroll-normal-white")}
            style={{
              minWidth: `calc(100% - ${2 * DEFAULT_LEFT}px)`,
              // width: DEFAULT_LEFT + temporaryEditedSessions.length * ((getColumnWidth(temporaryEditedSessions) + (5 * LEFT_OFFSET))),
              width: LEFT_OFFSET + sessions.length * getColumnWidth(sessions),
                // sessions.length * (getColumnWidth(sessions) + 5 * LEFT_OFFSET),
            }}
          >
            <TimeSlots width="100%" />

            <CurrentTimeHorizontalLine
              onlyLine
              date={date}
              ref={currenLine}
              leftOffset={OFFSET_LEFT}
              yAxisFromTop={calculateTopForCurrentTime}
              width={refTimeline.current?.getBoundingClientRect()?.width}
            />

            {isArray(sessions) && (
              <DailyCalendarColumns
                date={date}
                slots={slots}
                sessions={sessions}
                columnWidth={columnWidth}
                OFFSET_LEFT={OFFSET_LEFT}
              />
            )}
          </Grid>
        </Grid>
      {/*</div>*/}
    </Paper>
  );
};

EdReviewCalendarDaily.propTypes = {
  slots: PropTypes.array,
  sessions: PropTypes.array,
  handleSelect: PropTypes.func,
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default EdReviewCalendarDaily;
